import { apiClient } from './api';
import {
  ImagePromptSession,
  SessionStartResponse,
  IterationResponse,
  SessionStatusResponse,
  GenerateIterationRequest,
  ImageGenerationRequest,
} from '../models/imagePrompt';

export class ImagePromptService {
  private static readonly BASE_URL = '/api/imageprompt';
  private static readonly STORAGE_KEY = 'image_prompt_sessions_v2';

  // Start a new session
  static async startSession(initialPrompt: string, sessionType: string = 'Standard', nicheParams?: any): Promise<SessionStartResponse> {
    const response = await apiClient.post(`${this.BASE_URL}/session/start`, {
      InitialPrompt: initialPrompt,
      SessionType: sessionType,
      NicheParams: nicheParams
    });
    return response.data;
  }

  // Get session status
  static async getSession(sessionId: string): Promise<SessionStatusResponse> {
    const response = await apiClient.get(`${this.BASE_URL}/session/${sessionId}`);
    return response.data;
  }

  // Generate iteration
  static async generateIteration(sessionId: string, request: GenerateIterationRequest): Promise<IterationResponse> {
    const response = await apiClient.post(`${this.BASE_URL}/session/${sessionId}/iterate`, {
      AgentType: request.agentType,
      MaxResponseTokens: request.maxResponseTokens || 2048,
      Temperature: request.temperature || 0.7,
      AdditionalInstructions: request.additionalInstructions,
      SessionParams: request.sessionParams
    });
    return response.data;
  }

  // Bulk iteration
  static async bulkIterate(sessionId: string, iterationCount: number = 3): Promise<any> {
    const response = await apiClient.post(`${this.BASE_URL}/session/${sessionId}/bulk-iterate`, {
      IterationCount: iterationCount,
      MaxResponseTokens: 2048,
      Temperature: 0.7
    });
    return response.data;
  }

  // Generate image using new provider system (legacy - use generateImageForSession instead)
  static async generateImage(request: ImageGenerationRequest): Promise<{ imageUrl: string }> {
    // Convert old request format to new format
    const newRequest = {
      model: 'flux', // Default to flux for backward compatibility
      prompt: request.prompt,
      width: 1024,
      height: 1024,
      numImages: request.num_images || 1,
      seed: request.seed,
    };

    const response = await apiClient.post('/api/imagegeneration/generate', newRequest);

    // Return first image URL for backward compatibility
    return {
      imageUrl: response.data.images && response.data.images.length > 0
        ? response.data.images[0].url
        : ''
    };
  }

  // NEW: Generate image for session with auto-persistence
  static async generateImageForSession(
    sessionId: string,
    options?: {
      model?: string;
      width?: number;
      height?: number;
      numImages?: number;
      seed?: number;
      guidanceScale?: number;
      negativePrompt?: string;
    }
  ): Promise<{
    model: string;
    images: Array<{ url: string; contentType: string; metadata: any }>;
    assetId?: string;
    metadata: any;
  }> {
    const request = {
      model: options?.model || 'flux-pro',
      width: options?.width || 1024,
      height: options?.height || 1024,
      numImages: options?.numImages || 1,
      seed: options?.seed,
      guidanceScale: options?.guidanceScale,
      negativePrompt: options?.negativePrompt,
    };

    const response = await apiClient.post(
      `${this.BASE_URL}/session/${sessionId}/generate-image`,
      request
    );

    return response.data;
  }

  // Save to local storage
  static saveToLocalStorage(sessions: ImagePromptSession[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(sessions));
    } catch (error) {
      console.error('Error saving to local storage:', error);
    }
  }

  // Get from local storage
  static getFromLocalStorage(): ImagePromptSession[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (data) {
        return JSON.parse(data).map((session: any) => ({
          ...session,
          createdAt: new Date(session.createdAt),
          lastUpdatedAt: new Date(session.lastUpdatedAt),
          iterations: session.iterations.map((iter: any) => ({
            ...iter,
            timestamp: new Date(iter.timestamp),
          })),
        }));
      }
    } catch (error) {
      console.error('Error loading from local storage:', error);
    }
    return [];
  }

  // Copy to clipboard
  static async copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      // Fallback method
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  }

  // Download file
  static downloadFile(content: string, filename: string, contentType: string = 'application/json'): void {
    const blob = new Blob([content], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  // Save single session to local storage
  static saveSession(session: ImagePromptSession): void {
    const sessions = this.getFromLocalStorage();
    const existingIndex = sessions.findIndex(s => s.id === session.id);
    
    if (existingIndex >= 0) {
      sessions[existingIndex] = session;
    } else {
      sessions.push(session);
    }
    
    this.saveToLocalStorage(sessions);
  }

  // Load all sessions from local storage
  static loadSessions(): ImagePromptSession[] {
    return this.getFromLocalStorage();
  }

  // Delete a session from local storage
  static deleteSession(sessionId: string): void {
    const sessions = this.getFromLocalStorage();
    const filteredSessions = sessions.filter(session => session.id !== sessionId);
    this.saveToLocalStorage(filteredSessions);
  }

  // Update a session in local storage
  static updateSession(updatedSession: ImagePromptSession): void {
    const sessions = this.getFromLocalStorage();
    const index = sessions.findIndex(session => session.id === updatedSession.id);
    if (index !== -1) {
      sessions[index] = {
        ...updatedSession,
        lastUpdatedAt: new Date(),
      };
      this.saveToLocalStorage(sessions);
    }
  }

  // Export a session to JSON file
  static exportSession(session: ImagePromptSession): void {
    const exportData = {
      ...session,
      exportedAt: new Date().toISOString(),
    };
    const content = JSON.stringify(exportData, null, 2);
    const filename = `${session.title.replace(/[^a-z0-9]/gi, '_')}_${Date.now()}.json`;
    this.downloadFile(content, filename);
  }

  // New API methods for prompt management
  static async savePrompt(prompt: string, metadata?: any): Promise<{ promptId: string }> {
    const response = await apiClient.post(`${this.BASE_URL}/prompt/save`, {
      prompt,
      metadata
    });
    return response.data;
  }

  static async loadPrompt(promptId: string): Promise<{ prompt: string; metadata?: any }> {
    const response = await apiClient.get(`${this.BASE_URL}/prompt/${promptId}`);
    return response.data;
  }

  static async listPrompts(): Promise<Array<{ promptId: string; prompt: string; createdAt: string; metadata?: any }>> {
    const response = await apiClient.get(`${this.BASE_URL}/prompts/list`);
    return response.data;
  }

  static async downloadPrompt(promptId: string): Promise<void> {
    const response = await apiClient.get(`${this.BASE_URL}/prompt/${promptId}/download`, {
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prompt-${promptId}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  static async uploadPrompt(file: File): Promise<{ promptId: string }> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await apiClient.post(`${this.BASE_URL}/prompt/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }
}